import { cn } from "@omni/utils/styles";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useCartServiceApi } from "@/api/hooks/cartService.hook";
import {
    ReservedTransportSlot,
    ReservedTransportSlotRequest,
    TransportSlot
} from "@/api/hooks/cartService.types";
import { handleError } from "@/api/hooks/utils";
import { DefaultFor } from "@/api/mappers/default.mapper";
import UncontrolledAlert from "@/components/General/Alert/uncontrollerAlert.component";
import { RouterModal } from "@/components/General/Modal/routerModal";
import { NotAvailable } from "@/components/Modals/TransportServiceModal/components/notAvailable";
import { SlotSelect } from "@/components/Modals/TransportServiceModal/components/slotSelect";
import { useThunkDispatch } from "@/redux/dispatcher";
import { updateOrderDetails } from "@/redux/modules/api/data/orderDetail/orderDetail.module";
import { getOrderId } from "@/redux/modules/api/data/orderDetail/orderDetail.selectors";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { HeaderKeys } from "@/utils/enums";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

type Props = {
    reservationToken: string;
};

export const TransportServiceModal = ({ reservationToken }: Props) => {
    const { t } = useGranite();

    const { close: closeModal } = useModalRouter(ModalType.TransportService);

    const [activeSlot, setActiveSlot] = useState<TransportSlot>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [slotsRequested, setSlotsRequested] = useState(false);

    const dispatch = useThunkDispatch();

    const orderId = useSelector(getOrderId);

    const [{ data = [] }, loadReservationSlots] = useCartServiceApi(
        {
            url: `/orders/transport`,
            method: "GET",
            mapper: (data) => {
                return determineSlots(DefaultFor<Array<TransportSlot>>()(data));
            },
            headers: {
                [HeaderKeys.orderIdPublic]: orderId
            }
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    const [{ error, loading }, placeReservation] = useCartServiceApi<
        ReservedTransportSlotRequest,
        ReservedTransportSlot,
        ReservedTransportSlot
    >(
        {
            url: `/orders/reservations`,
            method: "POST",
            mapper: DefaultFor<ReservedTransportSlot>()
        },
        {
            manual: true,
            useCache: false
        }
    );

    useEffect(() => {
        if (!error || error === null) {
            setErrorMessage(null);
            return;
        }
        const { message } = handleError(error);
        if (message) {
            setErrorMessage(message);
        }
    }, [error]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!(orderId && !slotsRequested)) {
            return;
        }

        loadReservationSlots().then(() => {
            setSlotsRequested(true);
        });
    }, [orderId]);

    const determineSlots = (transportSlots: Array<TransportSlot>) => {
        const dates: Array<string> = [];
        const slots: Array<TransportSlot[]> = [];

        transportSlots.forEach((slot) => {
            if (dates.indexOf(slot.startDate.substring(0, 10)) === -1) {
                dates.push(slot.startDate.substring(0, 10));
            }
        });

        dates.forEach((date) => {
            slots.push(
                transportSlots.filter((slot) => slot.startDate.includes(date))
            );
        });

        return slots;
    };

    const handleSubmit = async () => {
        if (orderId && activeSlot && reservationToken) {
            placeReservation({
                data: {
                    orderId,
                    reservationToken,
                    slotStart: activeSlot.startDate,
                    slotEnd: activeSlot.endDate,
                    language: Config.getLocale()
                }
            }).then((action) => {
                dispatch(
                    updateOrderDetails({
                        reservation: action.data
                    })
                );

                closeModal();
            });
        }
    };

    const available = data.length > 0;

    return (
        <RouterModal
            modalType={ModalType.TransportService}
            title={
                available
                    ? t("transportService.modal.title")
                    : t("transportService.modal.title.noTransportAvailable")
            }
            withFooter={true}
            isConfirmDisabled={!available}
            cancelButtonText={t(`transportService.modal.cancel`)}
            confirmButtonText={
                available ? t(`transportService.modal.confirm`) : undefined
            }
            onConfirm={handleSubmit}
            isLoading={loading}
        >
            {errorMessage && (
                <UncontrolledAlert
                    color={"danger"}
                    fade={false}
                    className={cn("tw:mt-6")}
                >
                    {errorMessage}
                </UncontrolledAlert>
            )}
            {!available && <NotAvailable />}
            {available && (
                <SlotSelect
                    slots={data}
                    activeSlot={activeSlot}
                    onClick={setActiveSlot}
                />
            )}
        </RouterModal>
    );
};
